import { FC, useContext, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Input, TableColumnsType, Flex } from "antd";
import { AnyObject } from "antd/lib/_util/type";
import { ColumnsType } from "antd/lib/table";

import { locationList } from "@/apis/inventoryv2";
import useDataList from "@/hooks/useDataList";

import { CommonListParams } from "@/types/common";
import { LocationLockEnum, WLocation } from "@/types/inventoryV2";

import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { getLocationCode } from "@/pages/Warehouse/utils";
import { LockOutlined, UnlockOutlined } from "@ant-design/icons";
import { getLocationIdx } from "@/utils/wLocation";
import useLabelColor from "@/pages/Warehouse/pages/InventoryManage/hooks/useLabelColor";
import { DictContext } from "@/layout/context/DictContext";
import SelectLocationType from "./SelectLocationType";

interface LocationSelectContentProps {
  value?: string;
  onChange?: (location: WLocation) => void;
  disableLocked?: boolean;
  height?: string | number;
  locationType?: number | undefined;
  onLocationTypeChange?: (type: number | undefined) => void;
}

export const LocationSelectContent: FC<LocationSelectContentProps> = ({
  value,
  onChange,
  disableLocked = true,
  height = "600px",
  locationType: externalLocationType,
  onLocationTypeChange,
}) => {
  const { t } = useTranslation();
  const { getLabelColor } = useLabelColor();
  const { locationTypes } = useContext(DictContext);

  const tableRef = useRef<TableLayoutRef | null>(null);
  const [list, total, listLoading, { fetchList }] = useDataList<WLocation, [CommonListParams, number | undefined]>({
    fetchApi: locationList
  });
  const [selectedKey, setSelectedKey] = useState<string | undefined>(value);
  const [keyword, setKeyword] = useState<string>("");
  const [internalLocationType, setInternalLocationType] = useState<number | undefined>(externalLocationType);
  const abortControllerRef = useRef<AbortController | null>(null);

  const locationType = externalLocationType !== undefined ? externalLocationType : internalLocationType;

  // 同步外部 value 变化
  useEffect(() => {
    setSelectedKey(value);
  }, [value]);

  const handleLocationTypeChange = (type: number | undefined) => {
    if (externalLocationType !== undefined && onLocationTypeChange) {
      onLocationTypeChange(type);
    } else {
      setInternalLocationType(type);
    }
  };

  const fetchLocations = (offset: number, keyword: string, type?: number) => {
    const take = tableRef.current?.pageSize ?? DEFAULT_PAGE_SIZE;
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchList({
      body: keyword,
      pagination: { offset, take },
      controller,
    }, type);
  };

  // 挂载时加载数据
  useEffect(() => {
    fetchLocations(0, "", locationType);

    // 卸载时取消请求
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const columns: TableColumnsType<WLocation> = [
    {
      title: t("warehouse.locationIndex"),
      dataIndex: "l1",
      key: "l1",
      width: 120,
      render: (text, record) => <SText text={getLocationIdx(record)} />
    },
    {
      title: <SText text={t("warehouse.locationCode")} />,
      key: "locationCode",
      width: 240,
      fixed: "left",
      render: (_, record) => (
        <SText text={getLocationCode(record)} />
      ),
    },
    {
      title: t("warehouse.labels"),
      key: "labels",
      width: 300,
      render: (_, record) => (
        <Flex gap={4} wrap="wrap">
          {record.label1 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label1")} text={record.label1} />}
          {record.label2 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label2")} text={record.label2} />}
          {record.label3 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label3")} text={record.label3} />}
          {record.label4 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label4")} text={record.label4} />}
          {record.label5 && <SText tagStyle={{ flex: 1 }} textColor="#FFF" tagMode color={getLabelColor("label5")} text={record.label5} />}
        </Flex>
      )
    },
    {
      title: t('counting.lockState'),
      dataIndex: 'lockState',
      key: 'lockState',
      width: 120,
      render: (lockState: LocationLockEnum) => {
        const isLocked = lockState === LocationLockEnum.LOCKED;
        return (
          <SText tagMode
            tagIcon={isLocked ? <LockOutlined /> : <UnlockOutlined />}
            color={isLocked ? 'red' : 'green'}
            text={isLocked ? t('counting.counting') : t('counting.notCounting')}
          />
        );
      },
    },
    {
      title: t("inventory.locationType"),
      key: "type",
      width: 140,
      render: (_, record) => {
        let info = locationTypes.find(type => type.key === record.type);
        return (
          info && <SText text={info.desc} />
        )
      }
    },
    {
      title: t("common.desc"),
      dataIndex: "desc",
      key: "desc",
      width: 200,
      render: (text) => <SText text={text || ''} />
    },
  ];

  return (
    <div style={{ height }}>
      <STableLayout
        loading={listLoading}
        dataSource={list}
        rowKey="id"
        rowSelection={{
          type: "radio",
          selectedRowKeys: selectedKey ? [selectedKey] : [],
          getCheckboxProps: (record) => ({
            disabled: disableLocked && record.lockState === LocationLockEnum.LOCKED,
          }),
          onChange: (_, selectedRows) => {
            const location = selectedRows[0] as WLocation;
            setSelectedKey(location?.id);
            onChange?.(location);
          },
        }}
        columns={columns as ColumnsType<AnyObject>}
        scrollX={700}
        scroll={{ y: typeof height === 'number' ? height - 120 : 480 }}
        headerChildren={
          <Flex gap={8} style={{ width: "100%" }}>
            <SelectLocationType
              allowClear
              value={locationType}
              style={{ width: 200 }}
              onChange={handleLocationTypeChange}
            />
            <Input.Search
              enterButton
              placeholder={t("warehouse.searchLocation")}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onSearch={(searchValue) => {
                const controller = new AbortController();
                abortControllerRef.current = controller;
                fetchLocations(0, searchValue, locationType);
                tableRef?.current?.resetTablePage();
              }}
            />
          </Flex>
        }
        tableLayoutRef={tableRef}
        total={total}
        handleChange={(offset) => {
          const controller = new AbortController();
          abortControllerRef.current = controller;
          fetchLocations(offset, keyword, locationType);
        }}
      />
    </div>
  );
};

export default LocationSelectContent;
